import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Typography } from '@mui/material';
import { Vault } from '../../types/vaults';
import vaultArrow from '../../assets/vaultArrow.svg';
import arrowCircle from '../../assets/dashboard/arrowCircle.svg';
import usdcIcon from '../../assets/crypto/usdc.svg';
import usdtIcon from '../../assets/crypto/usdt.svg';
import ethIcon from '../../assets/crypto/eth.svg';
import cbbtcIcon from '../../assets/crypto/cbbtc.svg';
import xaumIcon from '../../assets/crypto/xaum.svg';
import { FriendlyFormatNumber } from '../../Utils';

// Constants
const CELL_BACKGROUND = 'rgba(255, 255, 255, 0.06)';
const HOVER_BACKGROUND = 'rgba(255, 255, 255, 0.15)';

const commonTypographyStyles = {
  fontFamily: 'Inter',
  fontSize: '1.25rem',
  fontWeight: 500,
  lineHeight: '1.75rem',
  color: '#FFFFFF'
};

const vaultNameTypographyStyles = {
  fontFamily: 'Outfit',
  fontSize: { xs: '1rem', sm: '1rem', md: '1rem', lg: '1.2rem', xl: '1.2rem', '2xl': '1.5rem' },
  fontWeight: 500,
  lineHeight: '1.0625rem',
  color: '#FFFFFF',
  flexGrow: 1
};

interface VaultTableRowProps {
  vault: Vault;
}

type SupportedAsset = 'usdc' | 'usdt' | 'eth' | 'cbbtc' | 'xaum';

const iconMap: Record<SupportedAsset, string> = {
  usdc: usdcIcon,
  usdt: usdtIcon,
  eth: ethIcon,
  cbbtc: cbbtcIcon,
  xaum: xaumIcon
};

const VaultTableRow = ({ vault }: VaultTableRowProps) => (
  <TableRow
    sx={{
      '&:hover': {
        '& .MuiTableCell-root': {
          background: `${HOVER_BACKGROUND} !important`
        },
        '& .circle-background': {
          opacity: '1 !important'
        }
      },
      '& .MuiTableCell-root': {
        border: 'none'
      },
      '& td:not(:first-of-type):not(:last-of-type)': {
        background: CELL_BACKGROUND
      },
      '& td:first-of-type, & td:last-of-type': {
        background: CELL_BACKGROUND,
        borderRadius: '1.5625rem'
      },
      '& td:first-of-type': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
      },
      '& td:last-of-type': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
      }
    }}
  >
    <TableCell
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem'
      }}
    >
      <div
        style={{
          width: '2.125rem',
          height: '2.125rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <img
          src={iconMap[vault.asset.toLowerCase() as SupportedAsset] || iconMap.usdc}
          alt={vault.name}
          style={{
            width: '1.5rem',
            height: '1.5rem'
          }}
        />
      </div>
      <Typography sx={vaultNameTypographyStyles}>{vault.name}</Typography>
    </TableCell>

    <TableCell>
      <Typography sx={commonTypographyStyles}>{Number(vault.apy.netApy).toFixed(2)}%</Typography>
    </TableCell>

    <TableCell>
      <Typography sx={commonTypographyStyles}>
        {vault.tvl === null || vault.tvl == 0 ? '-' : FriendlyFormatNumber(vault.tvl)}
      </Typography>
    </TableCell>

    <TableCell>
      <Typography sx={commonTypographyStyles}>{vault.multiplier}X</Typography>
    </TableCell>

    <TableCell
      sx={{
        textAlign: 'right',
        paddingRight: '1.25rem !important'
      }}
    >
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={`https://app.morpho.org/vault?vault=${vault.address}&network=mainnet`}
        style={{
          position: 'relative',
          display: 'inline-block'
        }}
      >
        <img
          src={arrowCircle}
          alt="circle"
          className="circle-background"
          style={{
            position: 'absolute',
            width: '2.5rem',
            height: '2.5rem',
            top: '45%',
            left: '45%',
            transform: 'translate(-50%, -50%)',
            opacity: 0,
            transition: 'opacity 0.2s'
          }}
        />
        <img
          src={vaultArrow}
          alt="vaultArrow"
          style={{
            width: '1.22rem',
            height: '1.27rem',
            position: 'relative',
            zIndex: 1
          }}
        />
      </a>
    </TableCell>
  </TableRow>
);

const headerTypographyStyles = {
  fontFamily: 'Inter',
  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
  fontWeight: 500,
  lineHeight: { xs: '1.25rem', sm: '1.375rem', md: '1.625rem' },
  color: '#FFFFFF'
};

export default function VaultsTable({ vaults }: { vaults: Vault[] }) {
  return (
    <TableContainer
      sx={{
        '& .MuiTable-root': {
          borderCollapse: 'separate',
          borderSpacing: '0 0.625rem'
        },
        overflow: 'hidden'
      }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              '& .MuiTableCell-root': {
                border: 'none'
              }
            }}
          >
            <TableCell></TableCell>
            <TableCell>
              <Typography sx={headerTypographyStyles}>APY</Typography>
            </TableCell>
            <TableCell>
              <Typography sx={headerTypographyStyles}>TVL</Typography>
            </TableCell>
            <TableCell>
              <Typography sx={headerTypographyStyles}>Multiplier</Typography>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vaults
            .filter((vault) => vault.name === 'Relend USDC')
            .map((vault) => (
              <VaultTableRow key={vault.name} vault={vault} />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
